import { ContactReplyDialog } from "@app/shared/components/Notifications/ContactReplyDialog";
import { NotificationListItem } from "@app/shared/components/Notifications/NotificationListItem";
import { NotificationsEmptyState } from "@app/shared/components/Notifications/NotificationsEmptyState";
import { useNotificationsBell } from "@app/shared/hooks/use-notifications-bell.hook";
import { URLS } from "@core/shared/utils";
import { Button } from "@design-system/components/Button";
import { BaseLink } from "@design-system/components/link";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@design-system/ui/dropdown-menu";
import { trans, plainTrans } from "@mongez/localization";
import { Bell, CheckCheck, ChevronLeft, Sparkles, Trash2 } from "lucide-react";

export function NotificationsBell() {
  const {
    clearAll,
    deleteNotification,
    direction,
    isLoggedIn,
    markAllAsRead,
    notifications,
    open,
    openNotification,
    recentNotifications,
    replyNotification,
    setOpen,
    setReplyNotification,
    unreadCount,
  } = useNotificationsBell();

  if (!isLoggedIn) return null;

  return (
    <>
      <DropdownMenu dir={direction} open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild>
          <Button
            variant="ghost"
            size="icon"
            className="relative rounded-full transition hover:bg-primary/10 hover:text-primary"
            title={plainTrans("notifications.title")}
            aria-label={plainTrans("notifications.title")}
          >
            <Bell className="size-5" />
            {unreadCount > 0 && (
              <span className="absolute -end-1 -top-1 flex h-5 min-w-5 items-center justify-center rounded-full border-2 border-white bg-rose-500 px-1 text-[10px] font-black leading-none text-white shadow-sm dark:border-slate-950">
                {unreadCount > 99 ? "99+" : unreadCount}
              </span>
            )}
          </Button>
        </DropdownMenuTrigger>

        <DropdownMenuContent
          align="end"
          sideOffset={10}
          className="w-[25rem] max-w-[calc(100vw-1rem)] overflow-hidden rounded-3xl border-slate-200 bg-white p-0 shadow-2xl shadow-slate-900/15 dark:border-slate-800 dark:bg-slate-950"
        >
          <header className="border-b border-slate-200 bg-gradient-to-br from-slate-50 via-white to-primary/5 p-4 dark:border-slate-800 dark:from-slate-900 dark:via-slate-950 dark:to-primary/10">
            <div className="flex items-start justify-between gap-3">
              <div className="flex items-center gap-3">
                <span className="flex h-11 w-11 shrink-0 items-center justify-center rounded-2xl bg-primary text-white shadow-lg shadow-primary/20">
                  <Bell className="h-5 w-5" />
                </span>
                <div className="text-start">
                  <h2 className="font-black text-slate-950 dark:text-white">
                    {trans("notifications.title")}
                  </h2>
                  <p className="mt-0.5 text-xs text-slate-500 dark:text-slate-400">
                    {unreadCount > 0
                      ? trans("notifications.unreadSummary", {
                          count: unreadCount,
                        })
                      : trans("notifications.allCaughtUp")}
                  </p>
                </div>
              </div>

              {unreadCount > 0 && (
                <span className="inline-flex items-center gap-1 rounded-full bg-primary/10 px-2.5 py-1 text-[11px] font-black text-primary">
                  <Sparkles className="h-3.5 w-3.5" />
                  {unreadCount}
                </span>
              )}
            </div>

            {notifications.length > 0 && (
              <div className="mt-4 flex flex-wrap items-center gap-2">
                {unreadCount > 0 && (
                  <button
                    type="button"
                    onClick={() => markAllAsRead()}
                    className="inline-flex h-9 items-center gap-1.5 rounded-full border border-primary/15 bg-white px-3 text-xs font-bold text-primary transition hover:border-primary/30 hover:bg-primary/5 dark:bg-slate-900"
                  >
                    <CheckCheck className="h-4 w-4" />
                    {trans("notifications.markAllAsRead")}
                  </button>
                )}
                <button
                  type="button"
                  onClick={() => clearAll()}
                  className="inline-flex h-9 items-center gap-1.5 rounded-full px-3 text-xs font-bold text-slate-500 transition hover:bg-red-50 hover:text-red-600 dark:hover:bg-red-950/40"
                >
                  <Trash2 className="h-4 w-4" />
                  {trans("notifications.clearAll")}
                </button>
              </div>
            )}
          </header>

          <div className="max-h-[26rem] overflow-y-auto overscroll-contain bg-slate-50/70 p-2 dark:bg-slate-950">
            {recentNotifications.length === 0 ? (
              <NotificationsEmptyState compact />
            ) : (
              <div className="space-y-1.5">
                {recentNotifications.map((notification) => (
                  <NotificationListItem
                    compact
                    key={notification.id}
                    notification={notification}
                    onDelete={deleteNotification}
                    onOpen={openNotification}
                  />
                ))}
              </div>
            )}
          </div>

          <footer className="border-t border-slate-200 bg-white p-2 dark:border-slate-800 dark:bg-slate-950">
            <BaseLink
              to={URLS.notifications.all}
              className="flex h-11 items-center justify-center gap-2 rounded-2xl text-sm font-black text-primary transition hover:bg-primary/5"
              onClick={() => setOpen(false)}
            >
              {trans("notifications.seeAll")}
              <ChevronLeft className="h-4 w-4 rtl:rotate-180" />
            </BaseLink>
          </footer>
        </DropdownMenuContent>
      </DropdownMenu>

      <ContactReplyDialog
        notification={replyNotification}
        onClose={() => setReplyNotification(null)}
      />
    </>
  );
}
